/**
 * CapacityTab — Tasks Reports Phase 3.
 *
 * Shows team-level workload balance, idle capacity, and per-employee active
 * load bars. Capacity target is "tasks per week" (configurable per org).
 */
import React, { useMemo, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, Scale, Coffee, Activity, Briefcase, ArrowUpDown,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

const buildParams = (filters) => {
  const p = { from_date: filters.fromDate, to_date: filters.toDate };
  // Note: assignee filter is intentionally NOT forwarded — Capacity is an
  // org-wide / dept-wide view by design (we group BY assignee inside).
  if (filters.clientId && filters.clientId !== 'all') p.client_id = filters.clientId;
  if (filters.department && filters.department !== 'all') p.department = filters.department;
  if (filters.taskType && filters.taskType !== 'all') p.task_type = filters.taskType;
  if (filters.subtype && filters.subtype !== 'all') p.subtype = filters.subtype;
  if (filters.priority && filters.priority !== 'all') p.priority = filters.priority;
  return p;
};

const Tile = ({ icon: Icon, label, value, suffix, hint, tone = 'neutral', testid }) => {
  const toneCls = {
    pink: 'text-pink-700', emerald: 'text-emerald-700', amber: 'text-amber-700',
    red: 'text-red-700', blue: 'text-blue-700', neutral: 'text-neutral-800',
  }[tone] || 'text-neutral-800';
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4 hover:shadow-md transition-shadow" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-medium text-neutral-500 uppercase tracking-wider">{label}</span>
        {Icon && <Icon className={`w-4 h-4 ${toneCls}`} />}
      </div>
      <div className={`text-[28px] font-semibold leading-none ${toneCls} tabular-nums`}>
        {value}{suffix && <span className="text-base font-normal text-neutral-400 ml-1">{suffix}</span>}
      </div>
      <div className="text-[11px] text-neutral-400 mt-2">{hint}</div>
    </div>
  );
};

// Per-employee horizontal load bar
const LoadBar = ({ row, maxLoad, teamAvg, targetPerWeek }) => {
  const isOver = row.active_load > teamAvg * 1.5;
  const isUnder = row.active_load === 0;
  const tone = isUnder ? 'bg-neutral-300' : isOver ? 'bg-red-500' : 'bg-pink-500';
  const widthPct = maxLoad > 0 ? Math.min(100, (row.active_load / maxLoad) * 100) : 0;
  // Render the team-avg marker
  const avgPct = maxLoad > 0 ? Math.min(100, (teamAvg / maxLoad) * 100) : 0;

  return (
    <div className="grid grid-cols-12 gap-3 items-center py-2 px-3 border-b border-neutral-50 hover:bg-neutral-50/50">
      <div className="col-span-3 min-w-0">
        <div className="text-[13px] text-neutral-800 truncate">{row.name}</div>
        <div className="text-[10px] text-neutral-500 truncate">{row.designation} · {row.department}</div>
      </div>
      <div className="col-span-5 relative">
        <div className="h-3 bg-neutral-100 rounded-full overflow-hidden relative">
          <div className={`h-full transition-all ${tone}`} style={{ width: `${widthPct}%` }} />
          {/* team-avg marker */}
          <div
            className="absolute top-0 h-3 w-0.5 bg-neutral-500"
            style={{ left: `${avgPct}%` }}
            title={`Team avg: ${teamAvg}`}
          />
        </div>
        <div className="text-[10px] text-neutral-400 mt-0.5 flex justify-between">
          <span>0</span>
          <span className="text-neutral-500">avg {teamAvg}</span>
          <span>{maxLoad}</span>
        </div>
      </div>
      <div className="col-span-1 text-right tabular-nums text-[13px] font-medium text-neutral-800">
        {row.active_load}
      </div>
      <div className="col-span-1 text-right tabular-nums text-[12px] text-neutral-600">
        {row.concurrent_clients}
      </div>
      <div className="col-span-1 text-right tabular-nums text-[12px] text-neutral-600">
        {row.completed}
      </div>
      <div className="col-span-1 text-right">
        <UtilizationPill v={row.utilization_pct} />
      </div>
    </div>
  );
};

const UtilizationPill = ({ v }) => {
  const tone =
    v >= 100 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
    v >= 70  ? 'bg-pink-50 text-pink-700 border-pink-200' :
    v >= 30  ? 'bg-amber-50 text-amber-700 border-amber-200' :
               'bg-neutral-50 text-neutral-500 border-neutral-200';
  return (
    <span className={`inline-flex px-1.5 py-0.5 text-[11px] tabular-nums rounded border ${tone}`}>
      {v}%
    </span>
  );
};

const CapacityTab = ({ filters }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState('load_desc'); // load_desc | load_asc | completed_desc | clients_desc | util_desc
  const params = useMemo(() => buildParams(filters), [filters]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/api/admin/reports/tasks/capacity`, { headers: auth(), params })
      .then((r) => !cancelled && setData(r.data))
      .catch((e) => !cancelled && toast.error(e.response?.data?.detail || 'Failed to load capacity metrics'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [params]);

  const t = data?.tiles || {};
  const rows = data?.per_employee || [];
  const teamAvg = t.team_avg_active || 0;
  const maxLoad = Math.max(1, ...rows.map((r) => r.active_load), teamAvg * 2);

  const sorted = useMemo(() => {
    const cp = [...rows];
    switch (sort) {
      case 'load_asc': cp.sort((a, b) => a.active_load - b.active_load); break;
      case 'completed_desc': cp.sort((a, b) => b.completed - a.completed); break;
      case 'clients_desc': cp.sort((a, b) => b.concurrent_clients - a.concurrent_clients); break;
      case 'util_desc': cp.sort((a, b) => b.utilization_pct - a.utilization_pct); break;
      case 'load_desc':
      default:
        cp.sort((a, b) => b.active_load - a.active_load);
    }
    return cp;
  }, [rows, sort]);

  if (loading && !data) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="bg-white border border-neutral-200 rounded-xl p-4 animate-pulse h-[110px]" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="capacity-tab">
      {/* Row 1 — org tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile
          icon={Activity} label="Team active load" value={t.team_active_load ?? 0}
          hint={`across ${t.team_size ?? 0} active members`} tone="pink"
          testid="capacity-tile-team-load"
        />
        <Tile
          icon={Users} label="Team avg" value={t.team_avg_active ?? 0}
          hint={`active tasks per person`} tone="blue"
          testid="capacity-tile-avg"
        />
        <Tile
          icon={Scale} label="Workload balance" value={t.workload_balance_score ?? 100} suffix="%"
          hint="higher = more even distribution"
          tone={t.workload_balance_score >= 80 ? 'emerald' : t.workload_balance_score >= 60 ? 'amber' : 'red'}
          testid="capacity-tile-balance"
        />
        <Tile
          icon={Coffee} label="Idle capacity" value={`${t.idle_capacity_pct ?? 0}%`}
          hint={`${t.idle_count ?? 0} of ${t.team_size ?? 0} have 0 active`}
          tone={t.idle_capacity_pct >= 30 ? 'red' : t.idle_capacity_pct >= 10 ? 'amber' : 'emerald'}
          testid="capacity-tile-idle"
        />
      </div>

      {/* Row 2 — utilization tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile
          icon={Activity} label="Team utilization" value={`${t.utilization_pct ?? 0}%`}
          hint={`of ${t.target_completed_in_window_per_person ?? '—'} target/person · ${t.weeks_in_window ?? '—'}w window`}
          tone={t.utilization_pct >= 80 ? 'emerald' : t.utilization_pct >= 50 ? 'amber' : 'red'}
          testid="capacity-tile-utilization"
        />
        <Tile
          icon={Briefcase} label="Capacity target" value={t.capacity_target_per_week ?? '—'}
          hint="tasks / person / week (org setting)" tone="neutral"
          testid="capacity-tile-target"
        />
      </div>

      {/* Per-employee load list */}
      <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden" data-testid="capacity-per-employee">
        <div className="px-4 py-3 border-b border-neutral-100 flex items-center justify-between">
          <div>
            <div className="text-[13px] font-semibold text-[#1a1a2e]">Per-employee workload</div>
            <div className="text-[11px] text-neutral-500 mt-0.5">
              Active load · concurrent clients · completed in window · utilization
            </div>
          </div>
          <div className="flex items-center gap-2 text-[11px]">
            <ArrowUpDown className="w-3 h-3 text-neutral-400" />
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              data-testid="capacity-sort"
              className="px-2 py-1 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-200"
            >
              <option value="load_desc">Active load ↓</option>
              <option value="load_asc">Active load ↑</option>
              <option value="completed_desc">Completed ↓</option>
              <option value="clients_desc">Concurrent clients ↓</option>
              <option value="util_desc">Utilization ↓</option>
            </select>
          </div>
        </div>

        {/* Header row */}
        <div className="grid grid-cols-12 gap-3 px-3 py-2 bg-neutral-50 text-[10px] uppercase tracking-wider font-medium text-neutral-500 border-b border-neutral-100">
          <div className="col-span-3">Employee</div>
          <div className="col-span-5">Active load</div>
          <div className="col-span-1 text-right">Active</div>
          <div className="col-span-1 text-right">Clients</div>
          <div className="col-span-1 text-right">Completed</div>
          <div className="col-span-1 text-right">Util.</div>
        </div>

        {sorted.length === 0 ? (
          <div className="p-10 text-center text-sm text-neutral-500">
            No active employees match the current filters.
          </div>
        ) : (
          <div>
            {sorted.map((r) => (
              <LoadBar
                key={r.assignee_id}
                row={r}
                maxLoad={maxLoad}
                teamAvg={teamAvg}
                targetPerWeek={t.capacity_target_per_week}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CapacityTab;
